<template>
  <div class="legal">
    <header class="legal-top">
      <div class="legal-wrap legal-top-row">
        <router-link to="/" class="legal-brand">{{ org.portalTitle }}</router-link>
        <nav class="legal-tabs" aria-label="Legal documents">
          <router-link v-for="d in documents" :key="d.key" :to="d.path" class="legal-tab" :class="{ 'legal-tab--on': !hub && d.key === doc.key }">
            {{ d.title }}
          </router-link>
        </nav>
      </div>
    </header>

    <main v-if="hub" class="legal-wrap legal-hub">
      <p class="legal-eyebrow">{{ org.agency.short || org.platform }} · Last updated {{ updated }}</p>
      <h1>Legal and privacy</h1>
      <p class="legal-summary">How {{ org.portal }} works, what it does with personal information and what it stores on your device.</p>
      <div class="legal-cards">
        <router-link v-for="d in built" :key="d.key" :to="d.path" class="legal-card">
          <span class="legal-card-title">{{ d.title }}</span>
          <span class="legal-card-text">{{ d.summary }}</span>
        </router-link>
      </div>
    </main>

    <main v-else class="legal-wrap legal-body">
      <aside class="legal-toc" aria-label="On this page">
        <p class="legal-toc-label">On this page</p>
        <a v-for="s in doc.sections" :key="s.id" :href="`#${s.id}`" @click.prevent="go(s.id)">{{ s.heading }}</a>
      </aside>

      <article class="legal-doc">
        <p class="legal-eyebrow">{{ org.agency.short || org.platform }} · Version {{ version }} · Last updated {{ updated }}</p>
        <h1>{{ doc.title }}</h1>
        <p class="legal-summary">{{ doc.summary }}</p>

        <section v-for="s in doc.sections" :key="s.id" :id="s.id" class="legal-section">
          <h2>{{ s.heading }}</h2>
          <template v-for="(b, i) in s.blocks">
            <p v-if="b.p" :key="`p${i}`">{{ b.p }}</p>
            <ul v-else-if="b.ul" :key="`u${i}`">
              <li v-for="(li, j) in b.ul" :key="j">{{ li }}</li>
            </ul>
            <div v-else-if="b.table" :key="`t${i}`" class="legal-table-wrap">
              <table class="legal-table">
                <thead><tr><th v-for="h in b.table.head" :key="h" scope="col">{{ h }}</th></tr></thead>
                <tbody>
                  <tr v-for="(row, r) in b.table.rows" :key="r">
                    <td v-for="(cell, c) in row" :key="c" :data-label="b.table.head[c]">{{ cell }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </template>
        </section>

        <footer class="legal-foot">
          <span>Also read:</span>
          <router-link v-for="d in others" :key="d.key" :to="d.path">{{ d.title }}</router-link>
          <router-link to="/legal">All legal documents</router-link>
        </footer>
      </article>
    </main>
  </div>
</template>

<script>
import org from "./org";
import { DOCUMENTS, UPDATED, VERSION } from "./policies";

/** /privacy, /terms, /cookies (route meta.legal) and /legal. Public: no sign-in needed. Text in ./policies.js. */
export default {
  data() {
    return { org, documents: DOCUMENTS, updated: UPDATED, version: VERSION };
  },
  computed: {
    hub() { return this.$route.meta.legal === "hub"; },
    built() { return DOCUMENTS.map((d) => ({ ...d.build(org), path: d.path })); },
    doc() {
      const key = this.$route.meta.legal || "privacy";
      const d = DOCUMENTS.find((x) => x.key === key) || DOCUMENTS[0];
      return d.build(org);
    },
    others() { return DOCUMENTS.filter((d) => d.key !== this.doc.key); },
  },
  watch: {
    doc: { immediate: true, handler(d) { document.title = `${this.hub ? "Legal and privacy" : d.title} | ${org.portalTitle}`; } },
  },
  mounted() {
    const id = (this.$route.hash || "").slice(1);
    if (id) this.$nextTick(() => this.go(id, false));
  },
  methods: {
    go(id, smooth = true) {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: smooth ? "smooth" : "auto", block: "start" });
      if (this.$route.hash !== `#${id}`) this.$router.replace({ hash: `#${id}` }).catch(() => {});
    },
  },
};
</script>

<style scoped>
/* A reading page: one column of text (~70ch) beside a sticky index; tables scroll inside their own box. */
.legal {
  --ink: #0f172a; --body: #334155; --muted: #64748b; --rule: #e2e8f0; --paper: #ffffff; --wash: #f8fafc; --accent: #1e3a5f; --link: #2563eb;
  min-height: 100vh; background: var(--wash); color: var(--body);
  font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; font-size: 15px; line-height: 1.65;
}
.legal-wrap { max-width: 1120px; margin: 0 auto; padding-left: max(16px, env(safe-area-inset-left, 0px)); padding-right: max(16px, env(safe-area-inset-right, 0px)); }
.legal-top { background: var(--accent); color: #fff; position: sticky; top: 0; z-index: 5; }
.legal-top-row { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; padding-block: 12px; }
.legal-brand { color: #fff; font-weight: 800; letter-spacing: 0.01em; text-decoration: none; }
.legal-tabs { display: flex; gap: 4px; flex-wrap: wrap; }
.legal-tab { color: rgba(255, 255, 255, 0.8); text-decoration: none; padding: 6px 12px; border-radius: 6px; font-size: 14px; font-weight: 600; }
.legal-tab:hover, .legal-tab:focus-visible { color: #fff; background: rgba(255, 255, 255, 0.12); outline: none; }
.legal-tab--on { color: var(--accent); background: #fff; }
.legal-body { display: grid; grid-template-columns: 240px minmax(0, 1fr); gap: 40px; padding-block: 32px 64px; }
.legal-toc { position: sticky; top: 76px; align-self: start; display: flex; flex-direction: column; gap: 2px; max-height: calc(100vh - 100px); overflow-y: auto; }
.legal-toc-label { font-size: 12px; text-transform: uppercase; letter-spacing: 0.08em; color: var(--muted); font-weight: 700; margin: 0 0 6px; }
.legal-toc a { color: var(--body); text-decoration: none; font-size: 14px; padding: 4px 8px; border-left: 2px solid var(--rule); }
.legal-toc a:hover, .legal-toc a:focus-visible { color: var(--link); border-left-color: var(--link); outline: none; }
.legal-doc { min-width: 0; max-width: 74ch; background: var(--paper); border: 1px solid var(--rule); border-radius: 10px; padding: 36px clamp(18px, 4vw, 48px); }
.legal-eyebrow { font-size: 12px; text-transform: uppercase; letter-spacing: 0.08em; color: var(--muted); font-weight: 600; margin: 0 0 8px; }
.legal-doc h1 { color: var(--ink); font-size: clamp(26px, 4vw, 34px); font-weight: 800; line-height: 1.2; margin: 0 0 12px; text-wrap: balance; }
.legal-summary { font-size: 16px; color: var(--body); margin: 0 0 8px; padding-bottom: 20px; border-bottom: 1px solid var(--rule); }
.legal-section { scroll-margin-top: 80px; padding-top: 22px; }
.legal-section h2 { color: var(--ink); font-size: 19px; font-weight: 700; margin: 0 0 10px; text-wrap: balance; }
.legal-section p { margin: 0 0 12px; }
.legal-section ul { margin: 0 0 12px; padding-left: 20px; display: grid; gap: 6px; }
.legal-table-wrap { overflow-x: auto; margin: 4px 0 16px; border: 1px solid var(--rule); border-radius: 8px; }
.legal-table { width: 100%; border-collapse: collapse; font-size: 14px; }
.legal-table th { text-align: left; background: var(--wash); color: var(--ink); font-weight: 700; padding: 10px 12px; border-bottom: 1px solid var(--rule); white-space: nowrap; }
.legal-table td { padding: 10px 12px; border-bottom: 1px solid var(--rule); vertical-align: top; }
.legal-table tr:last-child td { border-bottom: 0; }
.legal-table td:first-child { color: var(--ink); font-weight: 600; }
.legal-foot { display: flex; flex-wrap: wrap; gap: 8px 16px; margin-top: 32px; padding-top: 16px; border-top: 1px solid var(--rule); font-size: 14px; color: var(--muted); }
.legal-foot a, .legal-doc a { color: var(--link); }
.legal-hub { max-width: 880px; padding-block: 40px 64px; }
.legal-hub h1 { color: var(--ink); font-size: clamp(26px, 4vw, 34px); font-weight: 800; margin: 0 0 12px; }
.legal-cards { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 16px; margin-top: 20px; }
.legal-card { display: flex; flex-direction: column; gap: 8px; background: var(--paper); border: 1px solid var(--rule); border-radius: 10px; padding: 20px; text-decoration: none; color: var(--body); }
.legal-card:hover, .legal-card:focus-visible { border-color: var(--link); outline: none; }
.legal-card-title { color: var(--ink); font-weight: 700; font-size: 17px; }
.legal-card-text { font-size: 14px; }
@media (max-width: 860px) {
  .legal-body { grid-template-columns: 1fr; gap: 16px; padding-top: 16px; }
  .legal-toc { position: static; max-height: none; flex-direction: row; flex-wrap: wrap; gap: 6px; }
  .legal-toc-label { width: 100%; }
  .legal-toc a { border: 1px solid var(--rule); border-radius: 999px; padding: 2px 10px; background: var(--paper); font-size: 13px; }
}
@media (max-width: 560px) {
  /* Tables become label/value cards on phones. */
  .legal-table thead { display: none; }
  .legal-table tr { display: block; padding: 10px 12px; border-bottom: 1px solid var(--rule); }
  .legal-table td { display: block; padding: 2px 0; border: 0; }
  .legal-table td::before { content: attr(data-label) ": "; color: var(--muted); font-weight: 600; }
}
@media print {
  .legal { background: #fff; }
  .legal-top, .legal-toc, .legal-foot { display: none; }
  .legal-body { display: block; padding: 0; }
  .legal-doc { border: 0; padding: 0; max-width: none; }
}
@media (prefers-reduced-motion: reduce) { * { scroll-behavior: auto !important; } }
</style>
